/**
 * Environment configuration for the frontend
 * Handles production, UAT, and development environments
 */

export type Environment = 'production' | 'uat' | 'development' | 'test';

interface EnvironmentConfig {
  name: Environment;
  apiUrl: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  sentryDsn?: string;
  posthogApiKey?: string;
  posthogHost?: string;
  enableSentry: boolean;
  enablePostHog: boolean;
  enableDebug: boolean;
  isProduction: boolean;
  isUAT: boolean;
  isDevelopment: boolean;
}

// Get environment from Vite or determine from context
const getEnvironmentName = (): Environment => {
  // Check for explicit VITE_ENVIRONMENT variable
  const viteEnv = import.meta.env.VITE_ENVIRONMENT?.toLowerCase();
  if (viteEnv && ['production', 'uat', 'development', 'test'].includes(viteEnv)) {
    return viteEnv as Environment;
  }

  // Check if running on UAT URLs
  const currentUrl = window.location.hostname;
  if (currentUrl.includes('-uat.onrender.com')) {
    return 'uat';
  }

  // Default to Vite's MODE
  if (import.meta.env.MODE === 'production') {
    return 'production';
  }

  return 'development';
};

const environment = getEnvironmentName();

// Environment-specific configurations
const configs: Record<Environment, Partial<EnvironmentConfig>> = {
  production: {
    enableSentry: true,
    enableDebug: false,
  },
  uat: {
    enableSentry: true,
    enableDebug: true,
  },
  development: {
    enableSentry: import.meta.env.VITE_ENABLE_SENTRY_DEV === 'true',
    enableDebug: true,
  },
  test: {
    enableSentry: false,
    enableDebug: true,
  },
};

// Create the final configuration
export const env: EnvironmentConfig = {
  name: environment,
  apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  sentryDsn: environment === 'production' 
    ? import.meta.env.VITE_SENTRY_DSN_PROD 
    : import.meta.env.VITE_SENTRY_DSN_DEV,
  posthogApiKey: environment === 'production'
    ? import.meta.env.VITE_POSTHOG_API_KEY_PROD
    : import.meta.env.VITE_POSTHOG_API_KEY_DEV,
  posthogHost: environment === 'production'
    ? import.meta.env.VITE_POSTHOG_HOST_PROD || 'https://us.i.posthog.com'
    : import.meta.env.VITE_POSTHOG_HOST_DEV || 'https://us.i.posthog.com',
  enableSentry: configs[environment].enableSentry ?? false,
  enablePostHog: !!import.meta.env.VITE_POSTHOG_API_KEY_PROD || !!import.meta.env.VITE_POSTHOG_API_KEY_DEV,
  enableDebug: configs[environment].enableDebug ?? false,
  isProduction: environment === 'production',
  isUAT: environment === 'uat',
  isDevelopment: environment === 'development',
};

// Export helper functions
export const isProduction = () => env.isProduction;
export const isUAT = () => env.isUAT;
export const isDevelopment = () => env.isDevelopment;
export const getEnvironment = () => env.name;

// Environment-aware timeout configurations
export const timeouts = {
  // OAuth grace period (initial wait before showing session establishing)
  oauthGracePeriod: env.isUAT ? 3000 : (env.isDevelopment ? 5000 : 5000),
  
  // Task polling interval for status updates
  taskPollInterval: env.isUAT ? 3000 : (env.isDevelopment ? 2000 : 2000),
  
  // SSE reconnection delay
  sseReconnectDelay: env.isUAT ? 3000 : (env.isDevelopment ? 1000 : 2000),
  
  // Task stuck detection threshold
  taskStuckThreshold: env.isUAT ? 15000 : (env.isDevelopment ? 5000 : 10000),
  
  // OAuth callback timeout (max time to wait for callback)
  oauthCallbackTimeout: env.isUAT ? 30000 : (env.isDevelopment ? 20000 : 25000),
  
  // Task verification timeout
  taskVerificationTimeout: env.isUAT ? 6000 : (env.isDevelopment ? 2000 : 4000),
};

// Log environment info (only in non-production)
if (!env.isProduction) {
  console.log('Environment Configuration:', {
    environment: env.name,
    apiUrl: env.apiUrl,
    supabaseUrl: env.supabaseUrl,
    sentryEnabled: env.enableSentry,
    debugEnabled: env.enableDebug,
  });
}